import { useState } from 'react'
import { api } from '../api/client'
import { claudeCodeCommand, outcomeLabel } from '../lib/aiAccess'
import { formatDateTime, relativeAge } from '../lib/time'
import { useAsync } from '../lib/useAsync'
import './ai-access.css'

/** A command or an address, with the one button it needs. */
function CopyLine({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    await navigator.clipboard.writeText(value)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 2000)
  }
  return (
    <div className="ai-copy">
      <code aria-label={label}>{value}</code>
      <button type="button" className="button" onClick={() => void copy()}>
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

/** How to connect an AI client, the person's connections with Disconnect, and
 *  what those clients have asked for (backend/app/mcp). A platform admin sees
 *  everyone's: they are who would be asked. */
export function AiAccess() {
  const state = useAsync(() => api.aiAccess.get(), [])
  const [failed, setFailed] = useState<string>()
  const data = state.data

  const disconnect = async (id: number) => {
    try {
      setFailed(undefined)
      await api.aiAccess.disconnect(id)
      state.reload()
    } catch (error) {
      setFailed(error instanceof Error ? error.message : 'Not disconnected.')
    }
  }

  return (
    <section className="ai-page" aria-labelledby="ai-access-title">
      <header>
        <h1 id="ai-access-title">AI Access</h1>
        <p className="muted">
          Ask Claude about stations and readings, and have it set up alerts, calculated channels and
          projects, with exactly the rights your role gives you. It cannot change or delete a stored
          reading. Everything it does is recorded here.
        </p>
      </header>

      {state.error && <p className="error">{state.error}</p>}
      {failed && <p className="error">{failed}</p>}

      {data && (
        <>
          <div className="ai-panel">
            <h2>Connect</h2>
            <h3>VS Code, or the Code tab in Claude Desktop</h3>
            <p className="muted">Run this once in a terminal, then sign in with Microsoft when asked (<code>/mcp</code> starts it).</p>
            <CopyLine label="Command to add the platform" value={claudeCodeCommand(data.url)} />
            <h3>The Chat tab in Claude Desktop</h3>
            <p className="muted">Settings, Connectors, Add custom connector, and paste this address:</p>
            <CopyLine label="Connector address" value={data.url} />
            <p className="muted">
              Raw readings come as a file. In VS Code and the Code tab, Claude downloads and analyses it
              itself. In the Chat tab, Claude gives you a link: open it from the office and attach the file.
            </p>
          </div>

          <div className="ai-panel">
            <h2>{data.everyone ? 'Connections' : 'Your connections'}</h2>
            {data.grants.length === 0
              ? <p className="muted">Nothing is connected.</p>
              : (
                <table className="grid">
                  <thead>
                    <tr>
                      <th>Client</th>
                      {data.everyone && <th>Person</th>}
                      <th>Connected</th>
                      <th>Last used</th>
                      <th>Ends</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {data.grants.map((g) => (
                      <tr key={g.id}>
                        <td>{g.client}</td>
                        {data.everyone && <td>{g.email}</td>}
                        <td>{formatDateTime(g.created_at)}</td>
                        <td>{relativeAge(g.last_used_at)}</td>
                        <td>{formatDateTime(g.expires_at)}</td>
                        <td>
                          <button type="button" className="button" onClick={() => void disconnect(g.id)}>
                            Disconnect
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>

          <div className="ai-panel">
            <h2>{data.everyone ? 'Recent requests' : 'Your recent requests'}</h2>
            {data.calls.length === 0
              ? <p className="muted">No requests yet.</p>
              : (
                <table className="grid">
                  <thead>
                    <tr>
                      <th>When</th>
                      {data.everyone && <th>Person</th>}
                      <th>Client</th>
                      <th>Asked for</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.calls.map((c) => (
                      <tr key={c.id}>
                        <td>{formatDateTime(c.at)}</td>
                        {data.everyone && <td>{c.email}</td>}
                        <td>{c.client}</td>
                        <td><code>{c.tool}</code></td>
                        <td>{outcomeLabel(c.outcome)}{c.error ? <span className="muted">: {c.error}</span> : null}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        </>
      )}
    </section>
  )
}
